import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Spin,
  Table,
  Input,
  Menu,
  Dropdown,
  Tag,
  Switch,
  Typography,
  Statistic,
  Space,
  Badge,
  Select,
  Tooltip,
} from "antd";
import { useSelector } from "react-redux";
import { RiAddLine, RiFileList2Line, RiBookmark3Line } from "react-icons/ri";
import { FaHashtag } from "react-icons/fa";
import {
  FiPlus,
  FiMoreVertical,
  FiFolder,
  FiTag,
  FiTrendingUp,
  FiFileText,
} from "react-icons/fi";
import {
  FileTextOutlined,
  EditOutlined,
  TagsOutlined,
  FolderOutlined,
  RiseOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  SearchOutlined,
  PlusOutlined,
  FilterOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import styled from "styled-components";
import {
  getAllBlogsPaginatedSearch,
  deleteBlogById,
  updateBlogApprovalById,
} from "@api/blog/blog";

const { Title, Text } = Typography;

// Styled Components
const StyledCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .ant-card-head-title {
    font-weight: 600;
  }
`;

const MetricCard = styled(motion.div)`
  padding: 24px;
  border-radius: 12px;
  background: ${(props) => props.background || "#fff"};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;

  .metric-title {
    color: rgba(0, 0, 0, 0.45);
    font-size: 14px;
    margin-bottom: 8px;
  }

  .metric-value {
    font-size: 32px;
    font-weight: 600;
    color: ${(props) => props.valueColor || "rgba(0, 0, 0, 0.85)"};
    margin-bottom: 8px;
  }

  .metric-trend {
    font-size: 14px;
  }
`;

const { Search } = Input;

const BlogsList = ({ history }) => {
  const { user } = useSelector((state) => state.auth);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [filterApproved, setFilterApproved] = useState(null);
  const [filterFormat, setFilterFormat] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchText(search);
    setPagination({ current: page, pageSize, total: 0 });
    fetchBlogs(page, pageSize, search);
  }, []);

  const fetchBlogs = async (page, pageSize, searchText = "") => {
    setLoading(true);
    try {
      const res = await getAllBlogsPaginatedSearch(page, pageSize, searchText);
      setBlogs(res.data.blogs);
      setPagination((p) => ({
        ...p,
        total: res.data.paginationData.totalBlogs,
        current: res.data.paginationData.page,
        pageSize: res.data.paginationData.perPage,
      }));
    } catch (err) {
      console.error("Fetch blogs error", err);
    } finally {
      setLoading(false);
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);

    history.push({ search: searchParams.toString() });
    setPagination({ ...pagination, current: page, pageSize });
    fetchBlogs(page, pageSize, searchText);
  };

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 }); // Reset to first page
    fetchBlogs(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Function to handle blog update
  const handleBlogUpdate = (id) => {
    // If user is superadmin, redirect to blog update page
    if (user?.role === "superadmin") {
      history.push(`/super-admin/blog/${id}/update`);
    } else if (user?.role === "marketing") {
      history.push(`/marketing-admin/blog/${id}/update`);
    }
  };

  // Function to handle blog delete
  const handleBlogDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this blog?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteBlogById(id);
          fetchBlogs(pagination.current, pagination.pageSize);
        } catch (error) {
          console.error("Error deleting blog", error);
        }
      },
    });
  };

  // Function to handle blog approval
  const handleBlogApproval = async (checked, id) => {
    try {
      await updateBlogApprovalById(id, { approved: checked });
      fetchBlogs(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error updating blog approval", error);
    }
  };

  // Function to format date
  const formatDate = (date) => {
    if (!date) return "";
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  const randomColor = () => {
    return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
  };

  const menu = (
    <Menu className="blog-dropdown-menu">
      {/* Create Blog */}
      <Menu.Item
        key="1"
        icon={<FiPlus size={16} />}
        onClick={() => {
          if (user?.role === "superadmin") {
            history.push("/super-admin/blog/create");
          } else if (user?.role === "marketing") {
            history.push("/marketing-admin/blog/create");
          }
        }}
      >
        Create Blog
      </Menu.Item>
      {/* View Blogs */}
      <Menu.Item
        key="2"
        icon={<FiFileText size={16} />}
        onClick={() => {
          if (user?.role === "superadmin") {
            history.push("/super-admin/blogs");
          } else if (user?.role === "marketing") {
            history.push("/marketing-admin/blogs");
          }
        }}
      >
        View Blogs
      </Menu.Item>
      {/* Blog Categories */}
      <Menu.Item
        key="3"
        icon={<FiFolder size={16} />}
        onClick={() => {
          if (user?.role === "superadmin") {
            history.push("/super-admin/blogs/blog-categories");
          } else if (user?.role === "marketing") {
            history.push("/marketing-admin/blogs/blog-categories");
          }
        }}
      >
        Blog Categories
      </Menu.Item>
      {/* Blog Tags */}
      <Menu.Item
        key="4"
        icon={<FiTag size={16} />}
        onClick={() => {
          if (user?.role === "superadmin") {
            history.push("/super-admin/blogs/tags");
          } else if (user?.role === "marketing") {
            history.push("/marketing-admin/blogs/tags");
          }
        }}
      >
        Blog Tags
      </Menu.Item>
      {/* Blog Campaigns */}
      <Menu.Item
        key="5"
        icon={<FiTrendingUp size={16} />}
        onClick={() => {
          if (user?.role === "superadmin") {
            history.push("/super-admin/blog-campaigns");
          } else if (user?.role === "marketing") {
            history.push("/marketing-admin/blog-campaigns");
          }
        }}
      >
        Blog Campaigns
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title) =>
        title.length > 25 ? `${title.substring(0, 25)}...` : title,
    },
    {
      title: "Author",
      dataIndex: "author",
      key: "author",
      render: (author) => author?.name,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => <Tag color="blue">{category?.name}</Tag>,
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (tags) => (
        <>
          {tags?.map((tag) => (
            <Tag color={randomColor()} key={tag._id}>
              {tag?.name}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Format",
      dataIndex: "isMarkdown",
      key: "isMarkdown",
      render: (isMarkdown) =>
        isMarkdown ? (
          <Tag color="green">Markdown</Tag>
        ) : (
          <Tag color="orange">Rich Text</Tag>
        ),
    },
    {
      title: "Published At",
      dataIndex: "publishedDate",
      key: "publishedDate",
      render: (date) => formatDate(date),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => formatDate(date),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            type="primary"
            size="small"
            style={{ marginRight: "15px" }}
            onClick={() => handleBlogUpdate(record._id)}
          >
            Edit
          </Button>
          <Button
            type="danger"
            size="small"
            onClick={() => handleBlogDelete(record._id)}
          >
            Delete
          </Button>
        </>
      ),
    },
    // Approved
    {
      title: "Approved",
      dataIndex: "approved",
      key: "approved",
      // Render a switch to toggle blog approval
      render: (approved, record) => (
        <Switch
          checked={approved}
          onChange={(checked) => handleBlogApproval(checked, record._id)}
        />
      ),
    },
  ];

  // Show initial loading state
  if (loading && isInitialLoad) {
    return (
      <Card style={{ borderRadius: "0", height: "100vh", overflowY: "auto" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "70vh",
            padding: "64px",
          }}
        >
          {/* Animated Blog Icons */}
          <motion.div
            style={{
              display: "flex",
              gap: "24px",
              marginBottom: "48px",
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            {[
              { icon: <FileTextOutlined />, color: "#1890FF", delay: 0 },
              { icon: <EditOutlined />, color: "#52C41A", delay: 0.1 },
              { icon: <TagsOutlined />, color: "#FAAD14", delay: 0.2 },
              { icon: <FolderOutlined />, color: "#722ED1", delay: 0.3 },
              { icon: <RiseOutlined />, color: "#FF4D4F", delay: 0.4 },
            ].map((item, index) => (
              <motion.div
                key={index}
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "16px",
                  background: `linear-gradient(135deg, ${item.color}15 0%, ${item.color}30 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "28px",
                  color: item.color,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
                animate={{
                  y: [0, -12, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  delay: item.delay,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {item.icon}
              </motion.div>
            ))}
          </motion.div>

          {/* Spinner with Gradient Ring */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ marginBottom: "32px" }}
          >
            <div style={{ position: "relative", width: "80px", height: "80px" }}>
              <Spin
                size="large"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
              <motion.div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "50%",
                  border: "3px solid transparent",
                  borderTopColor: "#1890ff",
                  borderRightColor: "#52c41a",
                  opacity: 0.3,
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </motion.div>

          {/* Loading Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            style={{ textAlign: "center" }}
          >
            <Title
              level={3}
              style={{
                margin: 0,
                marginBottom: "12px",
                background: "linear-gradient(135deg, #1890ff 0%, #722ed1 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontWeight: 600,
              }}
            >
              Loading Blogs
            </Title>
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Text
                type="secondary"
                style={{
                  fontSize: "16px",
                  display: "block",
                }}
              >
                Fetching all your blog posts...
              </Text>
            </motion.div>
          </motion.div>

          {/* Progress Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            style={{
              marginTop: "48px",
              display: "flex",
              gap: "32px",
              alignItems: "center",
            }}
          >
            {[
              { icon: <FileTextOutlined />, label: "Loading Posts" },
              { icon: <TagsOutlined />, label: "Fetching Tags" },
              { icon: <FolderOutlined />, label: "Getting Categories" },
            ].map((item, index) => (
              <motion.div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.4,
                  delay: 1 + index * 0.2,
                  type: "spring",
                  stiffness: 200,
                }}
              >
                <motion.div
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 3,
                    delay: index * 0.3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    fontSize: "24px",
                    color: "#1890ff",
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #1890ff15 0%, #1890ff30 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </motion.div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {item.label}
                </Text>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Card>
    );
  }

  // Calculate stats from blogs data
  const approvedCount = blogs.filter((blog) => blog.approved).length;
  const pendingCount = blogs.filter((blog) => !blog.approved).length;
  const markdownCount = blogs.filter((blog) => blog.isMarkdown).length;
  const richTextCount = blogs.filter((blog) => !blog.isMarkdown).length;

  // Filter blogs based on filters
  const filteredBlogs = blogs.filter((blog) => {
    if (filterApproved !== null && blog.approved !== filterApproved) return false;
    if (filterFormat !== null && blog.isMarkdown !== filterFormat) return false;
    return true;
  });

  return (
    <div style={{ padding: "24px", background: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header Section */}
      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Title
            level={2}
            style={{
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <FileTextOutlined />
            Blogs Management
          </Title>

          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                if (user?.role === "superadmin") {
                  history.push("/super-admin/blog/create");
                } else if (user?.role === "marketing") {
                  history.push("/marketing-admin/blog/create");
                }
              }}
            >
              Create Blog
            </Button>
            <Dropdown overlay={menu} placement="bottomRight" trigger={["click"]}>
              <Button
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "4px 12px",
                }}
              >
                <FiMoreVertical size={18} />
              </Button>
            </Dropdown>
          </div>
        </div>

        {/* Search and Filters */}
        <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "24px" }}>
          <Search
            placeholder="Search by title or description..."
            prefix={<SearchOutlined />}
            style={{ width: 350 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
            allowClear
          />

          <Select
            placeholder="Filter by Status"
            allowClear
            style={{ minWidth: 150 }}
            value={filterApproved}
            onChange={setFilterApproved}
          >
            <Select.Option value={true}>
              <Space>
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
                Approved
              </Space>
            </Select.Option>
            <Select.Option value={false}>
              <Space>
                <ClockCircleOutlined style={{ color: "#faad14" }} />
                Pending
              </Space>
            </Select.Option>
          </Select>

          <Select
            placeholder="Filter by Format"
            allowClear
            style={{ minWidth: 150 }}
            value={filterFormat}
            onChange={setFilterFormat}
          >
            <Select.Option value={true}>
              <Tag color="green">Markdown</Tag>
            </Select.Option>
            <Select.Option value={false}>
              <Tag color="orange">Rich Text</Tag>
            </Select.Option>
          </Select>
        </div>

        {/* Key Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <MetricCard
                background="linear-gradient(135deg, #1890FF 0%, #096DD9 100%)"
                valueColor="#fff"
                animate={{ y: [50, 0], opacity: [0, 1] }}
                transition={{ duration: 0.5 }}
              >
                <Text
                  className="metric-title"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Total Blogs
                </Text>
                <div className="metric-value" style={{ color: "#fff" }}>
                  {pagination.total}
                </div>
                <div
                  className="metric-trend"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  <FileTextOutlined /> All blog posts
                </div>
              </MetricCard>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <MetricCard
                background="linear-gradient(135deg, #52C41A 0%, #389E0D 100%)"
                valueColor="#fff"
                animate={{ y: [50, 0], opacity: [0, 1] }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Text
                  className="metric-title"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Approved
                </Text>
                <div className="metric-value" style={{ color: "#fff" }}>
                  {approvedCount}
                </div>
                <div
                  className="metric-trend"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  <CheckCircleOutlined /> Published blogs
                </div>
              </MetricCard>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <MetricCard
                background="linear-gradient(135deg, #FAAD14 0%, #D48806 100%)"
                valueColor="#fff"
                animate={{ y: [50, 0], opacity: [0, 1] }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Text
                  className="metric-title"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Pending
                </Text>
                <div className="metric-value" style={{ color: "#fff" }}>
                  {pendingCount}
                </div>
                <div
                  className="metric-trend"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  <ClockCircleOutlined /> Awaiting approval
                </div>
              </MetricCard>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <MetricCard
                background="linear-gradient(135deg, #722ED1 0%, #531DAB 100%)"
                valueColor="#fff"
                animate={{ y: [50, 0], opacity: [0, 1] }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Text
                  className="metric-title"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Markdown
                </Text>
                <div className="metric-value" style={{ color: "#fff" }}>
                  {markdownCount}
                </div>
                <div
                  className="metric-trend"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  <EditOutlined /> Markdown format
                </div>
              </MetricCard>
            </Col>
          </Row>
        </motion.div>
      </div>

      {/* Table Section */}
      <StyledCard>
        <Spin spinning={loading && !isInitialLoad}>
          <Table
            columns={columns}
            dataSource={filteredBlogs}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} blogs`,
            }}
            loading={loading && !isInitialLoad}
            onChange={({ current, pageSize }) =>
              handlePaginationChange(current, pageSize)
            }
            rowKey={(record) => record._id}
            scroll={{ x: 1200 }}
          />
        </Spin>
      </StyledCard>
    </div>
  );
};

export default BlogsList;
